import {DataStatus} from 'platform/components';

import {
  usePatchDistraintRegisterConfigMutation,
  usePatchEuViesConfigMutation,
  usePatchInsolvencyRegisterConfigMutation,
  usePatchInvalidDocumentRegisterConfigMutation,
  usePatchUnreliableVatPayerRegisterConfigMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsStackItemProps, SettingsStackItems} from '@dms/settings/shared';
import {handleApiError, useAvailableVerificationRegisters, useTenant} from '@dms/shared';

import {buildArray} from 'shared/web';

export function CustomerVerificationList() {
  const {registers, isLoading, isError} = useAvailableVerificationRegisters();
  const {tenantCountry, isLoading: isTenantLoading, isError: isTenantError} = useTenant();
  const isCzTenant = tenantCountry === 'CZE';
  const [patchDistraintRegister] = usePatchDistraintRegisterConfigMutation();
  const [patchInsolvencyRegister] = usePatchInsolvencyRegisterConfigMutation();
  const [patchInvalidDocumentRegister] = usePatchInvalidDocumentRegisterConfigMutation();
  const [patchUnreliableVatPayerRegister] = usePatchUnreliableVatPayerRegisterConfigMutation();
  const [patchEuVies] = usePatchEuViesConfigMutation();

  const saveSwitchPosition = (mutation: typeof patchEuVies, enabled: boolean) => {
    mutation({body: {enabled}}).unwrap().catch(handleApiError);
  };

  const items = buildArray<SettingsStackItemProps>()
    .when(isCzTenant, {
      cyId: testIds.settings.customerVerificationServices('unreliablePayersRegister'),
      title: i18n.t('entity.customerVerification.labels.registers.unreliable_vat_payer'),
      switchProps: {
        name: 'customerVerification-unreliablePayersRegister',
        value: registers.includes('unreliable_vat_payer'),
        onChange: (enabled) => saveSwitchPosition(patchUnreliableVatPayerRegister, enabled),
      },
    })
    .when(isCzTenant, {
      cyId: testIds.settings.customerVerificationServices('insolvencyRegister'),
      title: i18n.t('entity.customerVerification.labels.registers.insolvency'),
      switchProps: {
        name: 'customerVerification-insolvencyRegister',
        value: registers.includes('insolvency'),
        onChange: (enabled) => saveSwitchPosition(patchInsolvencyRegister, enabled),
      },
    })
    .when(isCzTenant, {
      cyId: testIds.settings.customerVerificationServices('invalidDocumentRegister'),
      title: i18n.t('entity.customerVerification.labels.registers.invalid_document'),
      switchProps: {
        name: 'customerVerification-invalidDocumentRegister',
        value: registers.includes('invalid_document'),
        onChange: (enabled) => saveSwitchPosition(patchInvalidDocumentRegister, enabled),
      },
    })
    .add({
      cyId: testIds.settings.customerVerificationServices('euViesRegister'),
      title: i18n.t('entity.customerVerification.labels.registers.eu_vies'),
      switchProps: {
        name: 'customerVerification-euViesRegister',
        value: registers.includes('eu_vies'),
        onChange: (enabled) => saveSwitchPosition(patchEuVies, enabled),
      },
    })
    .when(isCzTenant, {
      cyId: testIds.settings.customerVerificationServices('centralExecutionRecords'),
      title: i18n.t('entity.customerVerification.labels.registers.distraint'),
      url: settingsRoutes.customerVerificationDistraints,
      switchProps: {
        name: 'customerVerification-centralExecutionRecords',
        value: registers.includes('distraint'),
        onChange: (enabled) => saveSwitchPosition(patchDistraintRegister, enabled),
      },
    });

  return (
    <DataStatus isLoading={isLoading || isTenantLoading} isError={isError || isTenantError}>
      <SettingsStackItems items={items} />
    </DataStatus>
  );
}
